import { ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import { UserId, ZonedDateTime } from '@csp/csp-common-model';
import {
  DeviceSessionCriteriaFieldV1,
  DeviceSessionQueryCriterionV1,
  DeviceSessionQueryOperatorV1,
  DeviceSessionQuerySortOrderV1,
  DeviceSessionQueryTypeV1,
  DeviceSessionSortFieldV1,
} from '@csp/dmdp-api-device-dto';
import { groupBy, maxBy, minBy } from 'lodash';
import { RequestWindow } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import { DeviceSessionStatus } from '../model/DeviceSessionStatus';
import { DeviceSessionQueryCriteria } from '../model/DeviceSessionQueryCriteria';
import { DeviceObservationRequestRef } from '../model/DeviceObservationRequestRef';
import { DeviceSessionQuerySort } from '../model/DeviceSessionQuerySort';

export const filterByDateRangeCriteria = (
  fromDate: ZonedDateTime,
  toDate: ZonedDateTime,
): DeviceSessionQueryCriterionV1[] => [
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_WINDOW_END,
      type: DeviceSessionQueryTypeV1.GREATER_THAN_EQ,
      values: [ZonedDateTimeFormatter.toZonedDateTimeString(fromDate)],
    },
  },
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_WINDOW_END,
      type: DeviceSessionQueryTypeV1.LESS_THAN_EQ,
      values: [ZonedDateTimeFormatter.toZonedDateTimeString(toDate)],
    },
  },
];

export const filterByRequestRefCriteria = (
  scheduleRequestRef: DeviceObservationRequestRef,
): DeviceSessionQueryCriterionV1[] => [
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_SCHEDULE_CODE,
      type: DeviceSessionQueryTypeV1.EQ,
      values: [scheduleRequestRef.scheduleCode],
    },
  },
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_REQUEST_CODE,
      type: DeviceSessionQueryTypeV1.EQ,
      values: [scheduleRequestRef.requestCode],
    },
  },
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_VERSION_CODE,
      type: DeviceSessionQueryTypeV1.EQ,
      values: [scheduleRequestRef.versionCode],
    },
  },
];

export const filterByRequestStatusCriterion = (requestStatus: string): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_STATUS,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [requestStatus],
  },
});

export const filterByOneOfRequestStatusCriterion = (requestStatuses: string[]): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_STATUS,
    type: DeviceSessionQueryTypeV1.IN,
    values: requestStatuses,
  },
});

export const filterByScheduledRequestCriterion = (): DeviceSessionQueryCriterionV1 =>
  filterByRequestStatusCriterion('SCHEDULED');

export const filterByUnscheduledRequestCriterion = (): DeviceSessionQueryCriterionV1 =>
  filterByRequestStatusCriterion('UNSCHEDULED');

export const filterByMissedRequestCriterion = (): DeviceSessionQueryCriterionV1 =>
  filterByRequestStatusCriterion('MISSED');

export const filterBySessionStatusCriterion = (sessionStatus: string): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.SESSION_STATUS,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [sessionStatus],
  },
});

export const filterByCompleteStatusCriterion = (): DeviceSessionQueryCriterionV1 =>
  filterBySessionStatusCriterion('COMPLETE');

export const filterByIncompleteStatusCriterion = (): DeviceSessionQueryCriterionV1 =>
  filterBySessionStatusCriterion('INCOMPLETE');

export const filterByUserIdCriterion = (userId: string): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.USER_ID,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [userId],
  },
});

const filterByUserIdsCriterion = (userIds: UserId[]): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.USER_ID,
    type: DeviceSessionQueryTypeV1.IN,
    values: userIds,
  },
});

const sortByTimestampDescOrder = (): DeviceSessionQuerySort => ({
  order: DeviceSessionQuerySortOrderV1.DESC,
  field: DeviceSessionSortFieldV1.SESSION_TIMESTAMP,
});

const filterByRequestWindowsCriterion = (requestWindows: RequestWindow[]): DeviceSessionQueryCriterionV1 => {
  const groupedByScheduleCode = groupBy(requestWindows, window => window.requestRefCodes.scheduleCode);
  return {
    operator: DeviceSessionQueryOperatorV1.OR,
    criteria: Object.keys(groupedByScheduleCode).map(scheduleCode => {
      const windowsWithSameScheduleCode = groupedByScheduleCode[scheduleCode] ?? [];
      const groupedByRequestCode = groupBy(windowsWithSameScheduleCode, window => window.requestRefCodes.requestCode);
      return {
        operator: DeviceSessionQueryOperatorV1.AND,
        criteria: [
          {
            fieldCriterion: {
              field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_SCHEDULE_CODE,
              type: DeviceSessionQueryTypeV1.EQ,
              values: [scheduleCode],
            },
          },
          {
            operator: DeviceSessionQueryOperatorV1.OR,
            criteria: Object.keys(groupedByRequestCode).map(requestCode => {
              const windowsWithSameRequestCode = groupedByRequestCode[requestCode] ?? [];
              const minRepeatIndex = minBy(windowsWithSameRequestCode, window => window.repeatIndex)?.repeatIndex;
              const maxRepeatIndex = maxBy(windowsWithSameRequestCode, window => window.repeatIndex)?.repeatIndex;
              return {
                operator: DeviceSessionQueryOperatorV1.AND,
                criteria: [
                  {
                    fieldCriterion: {
                      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_REQUEST_CODE,
                      type: DeviceSessionQueryTypeV1.EQ,
                      values: [requestCode],
                    },
                  },
                  {
                    fieldCriterion: {
                      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_REPEAT_INDEX,
                      type: DeviceSessionQueryTypeV1.GREATER_THAN_EQ,
                      values: [minRepeatIndex],
                    },
                  },
                  {
                    fieldCriterion: {
                      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_REPEAT_INDEX,
                      type: DeviceSessionQueryTypeV1.LESS_THAN_EQ,
                      values: [maxRepeatIndex],
                    },
                  },
                ],
              };
            }),
          },
        ],
      };
    }),
  };
};

const toInOrEq = (type: unknown[]): DeviceSessionQueryTypeV1 =>
  type.length > 1 ? DeviceSessionQueryTypeV1.IN : DeviceSessionQueryTypeV1.EQ;

const filterBySessionStatusesCriterion = (responseStatuses: DeviceSessionStatus[]): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.SESSION_STATUS,
    type: toInOrEq(responseStatuses),
    values: responseStatuses,
  },
});

const filterByInvalidatedFlagCriterion = (invalid: boolean): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.INVALIDATED,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [invalid],
  },
});

const filterByNonConsentDataFlagCriterion = (nonConsentData: boolean): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.NON_CONSENT_DATA,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [nonConsentData],
  },
});

const filterByDeviceSessionQueryCriteria = (criteria: DeviceSessionQueryCriteria): DeviceSessionQueryCriterionV1 => ({
  criteria: [
    Array.isArray(criteria.userId)
      ? filterByUserIdsCriterion(criteria.userId)
      : filterByUserIdCriterion(criteria.userId),
    criteria.invalidated !== undefined ? filterByInvalidatedFlagCriterion(criteria.invalidated) : undefined,
    criteria.nonConsentData !== undefined ? filterByNonConsentDataFlagCriterion(criteria.nonConsentData) : undefined,
    criteria.requestWindows ? filterByRequestWindowsCriterion(criteria.requestWindows) : undefined,
    criteria.sessionStatuses ? filterBySessionStatusesCriterion(criteria.sessionStatuses) : undefined,
  ].filter(isDefined),
});

export const DeviceSessionQueryFactory = {
  filterByDateRangeCriteria,
  filterByRequestRefCriteria,
  filterByRequestStatusCriterion,
  filterByOneOfRequestStatusCriterion,
  filterByScheduledRequestCriterion,
  filterByMissedRequestCriterion,
  filterBySessionStatusCriterion,
  filterByCompleteStatusCriterion,
  filterByIncompleteStatusCriterion,
  filterByUserIdCriterion,
  sortByTimestampDescOrder,
  filterByDeviceSessionQueryCriteria,
};
